import React, { useState } from "react";
import { Mail, Phone, MapPin, Linkedin, Send } from "lucide-react";
import { profile } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill in name, email and message." });
      return;
    }

    const subject = `Portfolio enquiry — ${form.name}${form.company ? " (" + form.company + ")" : ""}`;
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.company ? `Company: ${form.company}` : null,
      "",
      "Message:",
      form.message
    ].filter(Boolean);
    const mailto = `mailto:${profile.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(bodyLines.join("\n"))}`;

    window.location.href = mailto;

    toast({
      title: "Opening your email app…",
      description: `Your draft to ${profile.email} is ready. Send it to complete.`
    });
  };

  return (
    <section
      id="contact"
      className="py-24 lg:py-32 bg-ink text-ivory-50 relative grain-overlay overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-bronze" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-ivory-50/70">
                Contact
              </span>
            </div>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] font-light">
              Let's talk about your next service chapter.
            </h2>
            <p className="mt-6 text-ivory-50/70 leading-relaxed max-w-md">
              Whether you're scaling a managed‑services practice, rewriting a service P&amp;L, or
              looking for a senior PMO partner across the GCC — I'd be glad to exchange notes.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${profile.email}`}
                className="flex items-center gap-4 text-ivory-50/85 hover:text-bronze transition-colors"
              >
                <div className="h-11 w-11 rounded-sm border border-ivory-50/15 grid place-items-center">
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ivory-50/50">
                    Email
                  </div>
                  <div className="text-sm">{profile.email}</div>
                </div>
              </a>
              <a
                href={`tel:${profile.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-4 text-ivory-50/85 hover:text-bronze transition-colors"
              >
                <div className="h-11 w-11 rounded-sm border border-ivory-50/15 grid place-items-center">
                  <Phone className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ivory-50/50">
                    Phone
                  </div>
                  <div className="text-sm">{profile.phone}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 text-ivory-50/85">
                <div className="h-11 w-11 rounded-sm border border-ivory-50/15 grid place-items-center">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ivory-50/50">
                    Based in
                  </div>
                  <div className="text-sm">{profile.location}</div>
                </div>
              </div>
              <a
                href={profile.linkedin}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 text-ivory-50/85 hover:text-bronze transition-colors"
              >
                <div className="h-11 w-11 rounded-sm border border-ivory-50/15 grid place-items-center">
                  <Linkedin className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ivory-50/50">
                    LinkedIn
                  </div>
                  <div className="text-sm">Connect with me</div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-ivory-50 text-ink rounded-sm p-8 lg:p-10 border border-ivory-50/10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-ink/60 block mb-2">
                    Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-ink/20 focus:border-bronze outline-none py-2.5 text-sm transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-ink/60 block mb-2">
                    Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-ink/20 focus:border-bronze outline-none py-2.5 text-sm transition-colors"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="text-[11px] uppercase tracking-[0.22em] text-ink/60 block mb-2">
                  Company (optional)
                </label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-ink/20 focus:border-bronze outline-none py-2.5 text-sm transition-colors"
                  placeholder="Organisation"
                />
              </div>

              <div className="mt-5">
                <label className="text-[11px] uppercase tracking-[0.22em] text-ink/60 block mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  className="w-full bg-transparent border-b border-ink/20 focus:border-bronze outline-none py-2.5 text-sm resize-none transition-colors"
                  placeholder="Tell me a little about your context or the challenge you're solving..."
                />
              </div>

              <button
                type="submit"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-ink text-ivory-50 rounded-sm hover:bg-ink-700 transition-colors text-sm"
              >
                Send via email
                <Send className="h-4 w-4" />
              </button>

              <p className="mt-4 text-[11px] text-ink/50">
                Clicking send opens your email client with a pre‑filled draft to {profile.email}.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
